import type { ComponentType, ReactNode } from "react";
import { Link } from "react-router";
import { cn } from "@/lib/cn";

type Props = {
  /** Cor hex do destaque, só no ícone (faixa e brilho em degradê saíram: degradê é só de avatar). */
  cor: string;
  icone: ComponentType<{ className?: string }>;
  titulo: string;
  descricao?: string;
  /** Com destino, o cartão inteiro é um link. */
  para?: string;
  /** Estado da navegação (`<Link state>`), ex.: avisar a página que veio do Menu. */
  estado?: unknown;
  /** Canto superior direito, ao lado do ícone (ex: selo de status). */
  canto?: ReactNode;
  /** Abaixo da descrição (ex: etiquetas). */
  children?: ReactNode;
  apagado?: boolean;
  /** Menor, pra grade de duas colunas no celular: menos respiro e descrição em duas linhas. */
  compacto?: boolean;
  className?: string;
};

/** O cartão de Apps Pro, reaproveitado pela página Menu. */
export function CartaoComDestaque({ cor, icone: Icone, titulo, descricao, para, estado, canto, children, apagado, compacto, className }: Props) {
  const classes = cn(
    "group relative flex flex-col overflow-hidden rounded-2xl border transition-all duration-200 ease-out will-change-transform",
    compacto ? "p-3.5" : "p-5",
    "border-zinc-200 bg-white hover:border-zinc-300 hover:shadow-xl",
    "dark:border-white/[0.06] dark:bg-[#121216]",
    "dark:shadow-[0_1px_0_0_rgba(255,255,255,0.03)_inset,0_20px_40px_-20px_rgba(0,0,0,0.4)]",
    "hover:-translate-y-0.5",
    para && "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2",
    apagado && "opacity-70 hover:opacity-90",
    className,
  );

  const conteudo = (
    <>
      <div className="relative flex items-start justify-between">
        <div
          className={cn(
            "flex items-center justify-center rounded-xl ring-1 ring-inset ring-white/5",
            compacto ? "h-10 w-10" : "h-11 w-11",
          )}
          style={{ backgroundColor: `${cor}1a`, color: cor }}
        >
          <Icone className={compacto ? "h-5 w-5" : "h-[22px] w-[22px]"} />
        </div>
        {canto}
      </div>

      <h3
        className={cn(
          "relative font-bold leading-tight tracking-tight text-zinc-900 dark:text-foreground",
          compacto ? "mt-2.5 text-[14px]" : "mt-4 text-[15px]",
        )}
      >
        {titulo}
      </h3>
      {descricao && (
        <p
          className={cn(
            "relative mt-1 flex-1 text-zinc-500 dark:text-muted-foreground",
            compacto ? "line-clamp-2 text-[11.5px] leading-snug" : "mt-1.5 line-clamp-3 text-[12.5px] leading-relaxed",
          )}
        >
          {descricao}
        </p>
      )}
      {children}
    </>
  );

  return para ? (
    <Link to={para} state={estado} className={classes}>
      {conteudo}
    </Link>
  ) : (
    <div className={classes}>{conteudo}</div>
  );
}
